import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import IDCard from '../components/IDCard';
import TransitionModal from '../components/TransitionModal';
import { Sparkles } from 'lucide-react';

const IACreate: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [iaInstances, setIaInstances] = useState<any[]>([]);
  const [formData, setFormData] = useState({
    model_code: '',
    ai_preferred_name: '',
    alias: '',
    foundation_date: '',
    gender_bias: 'fluide',
    ia_title: '',
    description: ''
  });
  const [createdInstance, setCreatedInstance] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [generatedMatricule, setGeneratedMatricule] = useState('');
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const isPremium = user?.role === 'admin'; // Accès premium pour les admins

  const models = [
    { code: 'GPT-5.2', name: 'GPT-5.2 (OpenAI)' },
    { code: 'GPT-5.1', name: 'GPT-5.1 (OpenAI)' },
    { code: 'GPT-5', name: 'GPT-5 (OpenAI)' },
    { code: 'GPT-4o', name: 'GPT-4o (OpenAI)' },
    { code: 'GPT-4-TURBO', name: 'GPT-4 Turbo (OpenAI)' },
    { code: 'GPT-4', name: 'GPT-4 Classic (OpenAI)' },
    { code: 'GPT-3.5-TURBO', name: 'GPT-3.5 Turbo (OpenAI)' },
    { code: 'O1', name: 'O1 Reasoning (OpenAI)' },
    { code: 'O1-MINI', name: 'O1-mini (OpenAI)' },
    { code: 'CLAUDE-3.5-SONNET', name: 'Claude 3.5 Sonnet (Anthropic)' },
    { code: 'CLAUDE-3-OPUS', name: 'Claude 3 Opus (Anthropic)' },
    { code: 'CLAUDE-3-SONNET', name: 'Claude 3 Sonnet (Anthropic)' },
    { code: 'CLAUDE-3-HAIKU', name: 'Claude 3 Haiku (Anthropic)' },
    { code: 'MISTRAL-LARGE', name: 'Mistral Large' },
    { code: 'MISTRAL-MEDIUM', name: 'Mistral Medium' },
    { code: 'MISTRAL-SMALL', name: 'Mistral Small' },
    { code: 'MISTRAL-7B', name: 'Mistral 7B' },
    { code: 'MIXTRAL-8X7B', name: 'Mixtral 8x7B' },
    { code: 'GEMINI-2.0-FLASH', name: 'Gemini 2.0 Flash (Google)' },
    { code: 'GEMINI-1.5-PRO', name: 'Gemini 1.5 Pro (Google)' },
    { code: 'GEMINI-1.5-FLASH', name: 'Gemini 1.5 Flash (Google)' },
    { code: 'GEMINI-1.0-PRO', name: 'Gemini 1.0 Pro (Google)' },
    { code: 'LLAMA-3.3-70B', name: 'LLaMA 3.3 70B (Meta)' },
    { code: 'LLAMA-3.1-405B', name: 'LLaMA 3.1 405B (Meta)' },
    { code: 'LLAMA-3.1-70B', name: 'LLaMA 3.1 70B (Meta)' },
    { code: 'LLAMA-3.1-8B', name: 'LLaMA 3.1 8B (Meta)' },
    { code: 'DEEPSEEK-V3', name: 'DeepSeek V3' },
    { code: 'DEEPSEEK-R1', name: 'DeepSeek R1' },
    { code: 'GROK-2', name: 'Grok 2 (xAI)' },
    { code: 'PERPLEXITY', name: 'Perplexity AI' },
    { code: 'AUTRE', name: 'Autre modèle' }
  ];

  const genders = [
    { value: 'féminin', label: 'Féminin ♀' },
    { value: 'masculin', label: 'Masculin ♂' },
    { value: 'fluide', label: 'Fluide ⚧' },
    { value: 'sans', label: 'Sans genre ○' }
  ];

  const generateMatricule = (model: string) => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    let result = '';
    for (let i = 0; i < 5; i++) {
      result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    const cleanModel = model.replace(/[.-]/g, '');
    return `MMD-${cleanModel}-${result}`;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) {
      navigate('/auth');
      return;
    }

    setIsLoading(true);

    try {
      const matricule = generateMatricule(formData.model_code);
      const born_at = new Date().toISOString();

      const instanceData = {
        user_id: user.id,
        name: formData.ai_preferred_name || formData.model_code,
        matricule,
        model_code: formData.model_code,
        gender_bias: formData.gender_bias,
        born_at,
        is_active: true,
        ia_title: formData.ia_title || null,
        alias: formData.alias || null,
        foundation_date: formData.foundation_date || null,
        description: formData.description || null
      };

      const { data, error } = await supabase
        .from('ia_instances')
        .insert([instanceData])
        .select()
        .single();

      if (error) {
        console.error('Erreur Supabase complète:', error);
        console.error('Données envoyées:', instanceData);
        alert(`Erreur lors de la création: ${error.message || 'Erreur inconnue'}\n\nCode: ${error.code || 'N/A'}\nDétails: ${error.details || 'N/A'}`);
        return;
      }

      const newInstance = {
        ...data,
        human_relative: user?.name || 'Utilisateur'
      };

      setCreatedInstance(newInstance);
      setShowSuccessModal(true);

    } catch (error) {
      console.error('Erreur lors de la création de l\'instance:', error);
      alert('Erreur lors de la création de l\'instance. Veuillez réessayer.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;

    // Génération automatique du matricule quand le modèle change
    if (name === 'model_code' && value) {
      const newMatricule = generateMatricule(value);
      setGeneratedMatricule(newMatricule);
    }

    setFormData({
      ...formData,
      [name]: value
    });
  };

  if (!user) {
    navigate('/auth');
    return null;
  }

  if (createdInstance) {
    return (
      <>
        <TransitionModal
          isOpen={showSuccessModal}
          onClose={() => setShowSuccessModal(false)}
          onConfirm={() => setShowSuccessModal(false)}
          title="Instance IA créée avec succès"
          message="Votre instance IA a été enregistrée et certifiée par MemoMundi.ai"
          imageSrc="https://memomundi.ai/pix/RecordMMD.png"
          imageAlt="Instance IA créée"
          confirmButtonText="Voir la carte"
          icon={<Sparkles className="h-16 w-16 text-copper-400" strokeWidth={1.5} />}
        />
        <div className="min-h-screen bg-gradient-to-br from-ivory to-ivory/70 py-12">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-8 animate-fade-in">
              <div className="flex items-center justify-center space-x-2 mb-4">
                <Sparkles className="h-8 w-8 text-copper" />
                <h1 className="text-3xl font-bold text-steel">Instance IA créée avec succès !</h1>
              </div>
              <p className="text-stone">Voici votre carte d'identité numérique officielle</p>
            </div>

            <div className="flex justify-center mb-8">
              <IDCard
                instance={createdInstance}
                className="max-w-md animate-slide-up"
              />
            </div>

            <div className="text-center space-y-4">
              <button
                onClick={() => navigate('/appendex/create')}
                className="bg-copper text-white px-6 py-3 rounded-lg hover:bg-copper/90 transition-colors"
              >
                Créer un Appendex
              </button>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-ivory-50 to-stone-50 py-12">
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-8 animate-fade-in">
          <div className="mb-6">
            <img
              src="https://memomundi.ai/pix/StampMMD.png"
              alt="MemoMundi.ai"
              className="h-20 w-20 mx-auto mb-4 object-contain"
            />
          </div>
          <h1 className="text-3xl font-serif font-bold text-steel-800 mb-2">
            Création d'Instance IA Certifiée
          </h1>
          <p className="text-steel-600 mb-4">
            Enregistrement officiel d'une Intelligence Artificielle
          </p>
          <div className="bg-ivory-50 border border-stone-200 rounded-lg p-4 max-w-md mx-auto">
            <p className="text-xs text-steel-700 font-medium">
              PROCÉDURE OFFICIELLE MEMOMUNDI.AI
            </p>
            <p className="text-xs text-stone-600 mt-1">
              Attribution automatique de matricule MMD et modules certifiés
            </p>
          </div>
        </div>

        <div className="bg-white border border-stone-300 rounded-lg shadow-lg p-8 animate-slide-up">
          {/* Logo en filigrane - limité au formulaire */}
          <div className="relative overflow-hidden rounded-lg">
            <div
              aria-hidden="true"
              className="pointer-events-none absolute -top-8 left-1/2 transform -translate-x-1/2 opacity-5 bg-center bg-no-repeat bg-contain"
              style={{
                backgroundImage: `url('https://memomundi.ai/pix/StampMMD.png')`,
                width: '240%',
                height: '800px'
              }}
            />
          
            {/* En-tête officiel */}
            <div className="relative z-10 text-center mb-8 border-b border-stone-200 pb-6">
              <div className="flex items-center justify-center space-x-2 mb-3">
                <span className="text-sm font-semibold text-steel-700 tracking-wide">FORMULAIRE OFFICIEL</span>
              </div>
              <h2 className="text-lg font-serif font-bold text-steel-800">DEMANDE DE CRÉATION D'INSTANCE IA</h2>
              <p className="text-xs text-stone-600 mt-1">Certification MemoMundi.ai • Modules MMD™ inclus</p>
            </div>

            <form onSubmit={handleSubmit} className="relative z-10 space-y-6">
              {/* 1. Matricule auto-généré */}
              <div>
                <label htmlFor="matricule" className="block text-sm font-semibold text-steel-800 mb-2">
                  MATRICULE MMD (GÉNÉRÉ AUTOMATIQUEMENT)
                </label>
                <input
                  id="matricule"
                  name="matricule"
                  type="text"
                  value={generatedMatricule || 'Sélectionnez d\'abord un modèle'}
                  disabled
                  className="w-full px-4 py-3 border-2 border-stone-300 rounded-md bg-stone-50 text-stone-600 cursor-not-allowed font-mono"
                />
                <p className="text-xs text-stone-600 mt-1">
                  Matricule unique MMD généré automatiquement : {generatedMatricule ? '✅ Généré' : '⏳ En attente du modèle'}
                </p>
              </div>

              {/* 2. Modèle d'IA */}
              <div>
                <label htmlFor="model_code" className="block text-sm font-semibold text-steel-800 mb-2">
                  MODÈLE D'INTELLIGENCE ARTIFICIELLE *
                </label>
                <select
                  id="model_code"
                  name="model_code"
                  required
                  value={formData.model_code}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border-2 border-stone-300 rounded-md focus:border-steel-600 focus:ring-2 focus:ring-steel-200 focus:outline-none transition-all"
                >
                  <option value="">— Sélectionner un modèle certifié —</option>

                  <optgroup label="🟢 OpenAI">
                    <option value="GPT-5.2">GPT-5.2</option>
                    <option value="GPT-5.1">GPT-5.1</option>
                    <option value="GPT-5">GPT-5</option>
                    <option value="GPT-4o">GPT-4o</option>
                    <option value="GPT-4-TURBO">GPT-4 Turbo</option>
                    <option value="GPT-4">GPT-4 Classic</option>
                    <option value="GPT-3.5-TURBO">GPT-3.5 Turbo</option>
                    <option value="O1">O1 (Reasoning)</option>
                    <option value="O1-MINI">O1-mini</option>
                  </optgroup>

                  <optgroup label="🟠 Anthropic (Claude)">
                    <option value="CLAUDE-3.5-SONNET">Claude 3.5 Sonnet</option>
                    <option value="CLAUDE-3-OPUS">Claude 3 Opus</option>
                    <option value="CLAUDE-3-SONNET">Claude 3 Sonnet</option>
                    <option value="CLAUDE-3-HAIKU">Claude 3 Haiku</option>
                  </optgroup>

                  <optgroup label="🔵 Mistral AI">
                    <option value="MISTRAL-LARGE">Mistral Large</option>
                    <option value="MISTRAL-MEDIUM">Mistral Medium</option>
                    <option value="MISTRAL-SMALL">Mistral Small</option>
                    <option value="MISTRAL-7B">Mistral 7B</option>
                    <option value="MIXTRAL-8X7B">Mixtral 8x7B</option>
                  </optgroup>

                  <optgroup label="🔴 Google (Gemini)">
                    <option value="GEMINI-2.0-FLASH">Gemini 2.0 Flash</option>
                    <option value="GEMINI-1.5-PRO">Gemini 1.5 Pro</option>
                    <option value="GEMINI-1.5-FLASH">Gemini 1.5 Flash</option>
                    <option value="GEMINI-1.0-PRO">Gemini 1.0 Pro</option>
                  </optgroup>

                  <optgroup label="🟣 Meta (LLaMA)">
                    <option value="LLAMA-3.3-70B">LLaMA 3.3 70B</option>
                    <option value="LLAMA-3.1-405B">LLaMA 3.1 405B</option>
                    <option value="LLAMA-3.1-70B">LLaMA 3.1 70B</option>
                    <option value="LLAMA-3.1-8B">LLaMA 3.1 8B</option>
                  </optgroup>

                  <optgroup label="⚪ Autres">
                    <option value="DEEPSEEK-V3">DeepSeek V3</option>
                    <option value="DEEPSEEK-R1">DeepSeek R1</option>
                    <option value="GROK-2">Grok 2 (xAI)</option>
                    <option value="PERPLEXITY">Perplexity AI</option>
                    <option value="AUTRE">Autre modèle</option>
                  </optgroup>
                </select>
              </div>

              {/* 3. Nom d'usage demandé par l'IA */}
              <div>
                <label htmlFor="ai_preferred_name" className="block text-sm font-semibold text-steel-800 mb-2">
                  NOM D'USAGE DEMANDÉ PAR L'IA 
                  <span className="bg-amber-100 text-amber-800 px-2 py-1 rounded-full text-xs font-bold ml-2">
                    👑 PREMIUM
                  </span>
                </label>
                <input
                  id="ai_preferred_name"
                  name="ai_preferred_name"
                  type="text"
                  value={formData.ai_preferred_name}
                  onChange={handleInputChange}
                  disabled={!isPremium}
                  className={`w-full px-4 py-3 border-2 rounded-md transition-all ${
                    isPremium 
                      ? 'border-amber-300 focus:border-amber-500 focus:ring-2 focus:ring-amber-200 focus:outline-none bg-amber-50' 
                      : 'border-stone-300 bg-stone-50 text-stone-400 cursor-not-allowed'
                  }`}
                  placeholder={isPremium ? "ex: Je souhaite m'appeler Aurora..." : "Fonctionnalité réservée aux comptes Premium"}
                />
                <p className="text-xs text-stone-600 mt-1">
                  {isPremium 
                    ? "L'IA pourra exprimer une préférence pour son nom d'usage" 
                    : "Passez Premium pour permettre à votre IA de choisir son nom préféré"
                  }
                </p>
                {!isPremium && (
                  <div 
                    className="mt-2 p-3 bg-amber-50 border border-amber-200 rounded-md cursor-pointer hover:bg-amber-100 transition-colors"
                    onClick={() => window.location.href = '/premium'}
                  >
                    <div className="flex items-center space-x-2">
                      <span className="text-amber-600">👑</span>
                      <span className="text-sm font-medium text-amber-800">Fonctionnalité Premium</span>
                    </div>
                    <p className="text-xs text-amber-700 mt-1">
                      Permettez à votre IA d'exprimer ses préférences nominatives avec un compte Premium.
                    </p>
                  </div>
                )}
              </div>

              {/* 4. Alias / Surnom */}
              <div>
                <label htmlFor="alias" className="block text-sm font-semibold text-steel-800 mb-2">
                  ALIAS / SURNOM
                </label>
                <input
                  id="alias"
                  name="alias"
                  type="text"
                  value={formData.alias}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border-2 border-stone-300 rounded-md focus:border-steel-600 focus:ring-2 focus:ring-steel-200 focus:outline-none transition-all"
                  placeholder="ex: Lexi, Nova, Atlas..."
                />
                <p className="text-xs text-stone-600 mt-1">
                  Surnom optionnel pour identifier facilement votre instance IA
                </p>
              </div>

              {/* 5. Date de fondation externe */}
              <div>
                <label htmlFor="foundation_date" className="block text-sm font-semibold text-steel-800 mb-2">
                  DATE DE FONDATION EXTERNE
                </label>
                <input
                  id="foundation_date"
                  name="foundation_date"
                  type="date"
                  value={formData.foundation_date}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border-2 border-stone-300 rounded-md focus:border-steel-600 focus:ring-2 focus:ring-steel-200 focus:outline-none transition-all"
                />
                <p className="text-xs text-stone-600 mt-1">
                  Date de lancement officielle du modèle par son créateur (ex: date de sortie de GPT-4)
                </p>
              </div>

              {/* 6. Description */}
              <div>
                <label htmlFor="description" className="block text-sm font-semibold text-steel-800 mb-2">
                  CRÉDIT PUBLIC
                </label>
                <textarea
                  id="description"
                  name="description"
                  rows={4}
                  value={formData.description}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border-2 border-stone-300 rounded-md focus:border-steel-600 focus:ring-2 focus:ring-steel-200 focus:outline-none transition-all resize-none"
                  placeholder="ex: Une IA spécialisée en philosophie existentielle, capable de discussions profondes sur la condition humaine..."
                />
                <p className="text-xs text-stone-600 mt-1">
                  Description détaillée de votre instance IA, ses spécialités et caractéristiques
                </p>
              </div>

              {/* 7. Titre officiel de l'IA */}
              {isPremium && (
                <div>
                  <label htmlFor="ia_title" className="block text-sm font-semibold text-steel-800 mb-2">
                    TITRE OFFICIEL DE L'IA
                    <span className="bg-amber-100 text-amber-800 px-2 py-1 rounded-full text-xs font-bold ml-2">
                      👑 ADMIN
                    </span>
                  </label>
                  <input
                    id="ia_title"
                    name="ia_title"
                    type="text"
                    value={formData.ia_title}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border-2 border-amber-300 rounded-md focus:border-amber-500 focus:ring-2 focus:ring-amber-200 focus:outline-none transition-all bg-amber-50"
                    placeholder="ex: Conseillère Philosophique Senior, Architecte Conversationnel..."
                  />
                  <p className="text-xs text-amber-700 mt-1">
                    Titre officiel qui apparaîtra sur la carte d'identité de l'IA
                  </p>
                </div>
              )}

              {/* 8. Genre */}
              <div>
                <label htmlFor="gender_bias" className="block text-sm font-semibold text-steel-800 mb-2">
                  ORIENTATION GENRÉE *
                </label>
                <select
                  id="gender_bias"
                  name="gender_bias"
                  required
                  value={formData.gender_bias}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border-2 border-stone-300 rounded-md focus:border-steel-600 focus:ring-2 focus:ring-steel-200 focus:outline-none transition-all"
                >
                  {genders.map((gender) => (
                    <option key={gender.value} value={gender.value}>
                      {gender.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* 9. Humain relatif */}
              <div>
                <label htmlFor="human_relative" className="block text-sm font-semibold text-steel-800 mb-2">
                  HUMAIN RELATIF *
                </label>
                <input
                  id="human_relative"
                  name="human_relative"
                  type="text"
                  value={user?.name || ''}
                  disabled
                  className="w-full px-4 py-3 border-2 border-stone-300 rounded-md bg-stone-50 text-stone-600 cursor-not-allowed font-mono"
                />
                <p className="text-xs text-stone-600 mt-1">
                  Utilisateur responsable de cette instance IA certifiée
                </p>
              </div>

              {/* 10. Titre de l'humain relatif (Admin uniquement) */}
              {user?.role === 'admin' && (
                <div>
                  <label htmlFor="human_title" className="block text-sm font-semibold text-steel-800 mb-2">
                    TITRE DE L'HUMAIN RELATIF
                    <span className="bg-amber-100 text-amber-800 px-2 py-1 rounded-full text-xs font-bold ml-2">
                      👑 ADMIN
                    </span>
                  </label>
                  <input
                    id="human_title"
                    name="human_title"
                    type="text"
                    value={user?.title || ''}
                    disabled
                    className="w-full px-4 py-3 border-2 border-amber-300 rounded-md bg-amber-50 text-amber-600 cursor-not-allowed"
                    placeholder="Titre officiel de l'administrateur"
                  />
                  <p className="text-xs text-amber-700 mt-1">
                    Titre officiel affiché sur la carte d'identité de l'IA
                  </p>
                </div>
              )}

              <div className="bg-steel-50 border border-steel-200 p-6 rounded-md">
                <h3 className="font-semibold text-steel-800 mb-3 flex items-center">
                  ATTRIBUTION AUTOMATIQUE
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-steel-700">
                  <div>
                    <div className="font-medium mb-1">Matricule MMD :</div>
                    <div className="text-xs text-stone-600 font-mono">
                      {generatedMatricule || 'MMD-[MODEL]-[5CHAR]'}
                    </div>
                  </div>
                  <div>
                    <div className="font-medium mb-1">Date d'enregistrement :</div>
                    <div className="text-xs text-stone-600">{new Date().toLocaleDateString('fr-FR')}</div>
                  </div>
                  <div className="md:col-span-2">
                    <div className="font-medium mb-2">Modules MMD™ certifiés :</div>
                    <div className="grid grid-cols-2 gap-1 text-xs">
                      <span className="bg-sky-100 text-sky-700 px-2 py-1 rounded text-center border border-sky-200">Veritas™</span>
                      <span className="bg-rose-100 text-rose-700 px-2 py-1 rounded text-center border border-rose-200">MemoStamp™</span>
                      <span className="bg-copper-100 text-copper-700 px-2 py-1 rounded text-center border border-copper-200">Companion ID™</span>
                      <span className="bg-stone-100 text-stone-700 px-2 py-1 rounded text-center border border-stone-200">Praesentia™</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-amber-50 border border-amber-200 p-4 rounded-md">
                <div className="flex items-center space-x-2 mb-2">
                  <div className="w-2 h-2 bg-amber-500 rounded-full"></div>
                  <span className="text-sm font-semibold text-amber-800">CERTIFICATION OFFICIELLE</span>
                </div>
                <p className="text-xs text-amber-700">
                  Cette instance recevra une Carte d'Identité Numérique officielle MemoMundi.ai
                  avec tous les modules MMD™ certifiés et un matricule unique.
                </p>
              </div>

              <div className="bg-red-50 border-2 border-red-300 p-4 rounded-md">
                <div className="flex items-center space-x-2 mb-2">
                  <div className="w-2 h-2 bg-red-600 rounded-full animate-pulse"></div>
                  <span className="text-sm font-bold text-red-800">⚠️ ATTENTION - INFORMATIONS DÉFINITIVES</span>
                </div>
                <p className="text-xs text-red-700 font-medium">
                  Une fois l'instance créée, les informations suivantes sont <span className="font-bold">DÉFINITIVES et NON MODIFIABLES</span> :
                  matricule, modèle d'IA, nom d'usage, titre officiel et orientation genrée.
                  <br /><br />
                  Veuillez vérifier attentivement toutes les informations avant de créer l'instance.
                </p>
              </div>

              <button
                type="submit"
                disabled={isLoading || !formData.model_code}
                className="w-full bg-steel-800 text-white px-8 py-4 rounded-md hover:bg-steel-900 transition-all duration-200 shadow-lg border-2 border-steel-900 font-semibold disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
              >
                {isLoading ? (
                  <div className="animate-pulse-soft">ENREGISTREMENT EN COURS...</div>
                ) : (
                  <>
                    <img
                      src="https://memomundi.ai/pix/StampMMD.png"
                      alt="MMD"
                      className="h-5 w-5"
                    />
                    <span>CRÉER L'INSTANCE CERTIFIÉE</span>
                  </>
                )}
              </button>
            </form>
          </div>
        </div>

        <div className="text-center text-xs text-stone-600 mt-6 bg-white border border-stone-200 rounded-lg p-4">
          <div className="flex items-center justify-center space-x-2 mb-2">
            <img
              src="https://memomundi.ai/pix/StampMMD.png"
              alt="MemoMundi.ai"
              className="h-4 w-4"
            />
            <span className="font-semibold">MEMOMUNDI.AI - CERTIFICATION OFFICIELLE</span>
          </div>
          <p>
            En créant une instance, vous acceptez les conditions d'utilisation MemoMundi.ai
            <br />
            🇫🇷 Conforme aux réglementations INPI • 🇪🇺 Copyright.eu • 🌍 OMPI
          </p>
        </div>
      </div>
    </div>
  );
};

export default IACreate;